/**
 * Feeds the signed-in tenancy's customer feature flags onto the app's flag store.
 *
 * ## Adding and reading a flag
 *
 * 1. **Declare it twice, identically** — `commons/features/__init__.py`
 *    (`FEATURE_FLAGS`) and `@authe/fe-commons/features` (`FEATURE_FLAGS`), with
 *    its default, kind and owner. `featuresParity.test.ts` fails if they drift;
 *    a name read anywhere must be declared.
 * 2. **Read it** with `useFeatureFlag("my_flag")` (or `<Flagged name="my_flag">`)
 *    from `@authe/ui-core/flags`. Nothing else: not this component, not the
 *    source, not the response.
 * 3. **Set it** per customer / org / tenant / user in the admin portal (a
 *    client-config `features` row; internal-only to write). The most specific
 *    rung wins; unset rungs inherit; nothing set = the declared default.
 *
 * A feature flag answers "is this rolled out to them?". "Did they pay for it?"
 * is a plan entitlement (`useEntitled`), and "may this person do it?" is a scope
 * (`usePermissions`) — gate on those, not on a flag.
 *
 * ## How the layer is fed
 *
 * One read of management-api `GET /features` per tenancy — react-query, keyed by
 * the full customer/org/tenant/user identity, so a tenant or org switch reads
 * afresh — then again every {@link FEATURE_FLAGS_FRESH_MS} while the tab is
 * visible, and on focus once that long has passed. It used to be read once per
 * session, which made every flag a switch that only worked FORWARDS: turning a
 * broken feature off reached nobody until they reloaded. Its answer is
 * pushed through `featureFlagLayer` (declared flags only, defaults filled) onto
 * `FeatureFlags.setClaims` — the layer the JWT `feature_flags` claim used to
 * occupy, which nothing ever wrote. Precedence is unchanged:
 * `override > policy > claim (these flags) > default`, so a `?debug=1` override
 * still wins for support.
 *
 * Fails to the DEFAULTS, never to off: while the read is in flight, after it
 * fails, and for a declared flag the response omits, the layer holds each flag's
 * declared default. A transient refetch failure keeps the tenancy's last good
 * answer rather than flipping every flag back.
 *
 * Renders nothing. Mounted inside `<FlagsProvider>` (it writes that store) and
 * only when a session exists (the read needs a tenancy).
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useFeatureFlags } from "@authe/ui-core/flags";
import {
  FEATURE_FLAGS,
  featureFlagLayer,
  type FeatureFlagDefinition,
} from "@authe/fe-commons/features";
import type { StorageIdentity } from "@authe/fe-commons/storage";
import { useFeaturesSource } from "@/api/adapters";
import { useStorageIdentity } from "./storageIdentity";

/** One entry per tenancy: a switch reads afresh instead of reusing the last answer. */
export function featureFlagsQueryKey(identity: StorageIdentity) {
  return [
    "feature-flags",
    identity.customerId,
    identity.orgId,
    identity.tenantId,
    identity.userId,
  ] as const;
}

export interface DashboardFeatureFlagsProps {
  /** The declared flags. Defaults to the shared registry; tests pass their own. */
  registry?: readonly FeatureFlagDefinition[];
}

/**
 * How long a tenancy's flags are trusted. A flag is also a kill switch, so an
 * operator's change has to reach the sessions already open — within minutes,
 * not at their next sign-in. One small GET per visible tab per interval.
 */
export const FEATURE_FLAGS_FRESH_MS = 5 * 60_000;

/**
 * The one read. Every observer shares its cache entry; only the `<DashboardFeatureFlags>`
 * observer keeps it fresh — a `passive` one (a page asking whether the read has
 * settled) must not start a second timer for the same GET.
 */
function useTenancyFeatureFlags({ passive = false }: { passive?: boolean } = {}) {
  const source = useFeaturesSource();
  const identity = useStorageIdentity();
  return useQuery({
    queryKey: featureFlagsQueryKey(identity),
    queryFn: ({ signal }) => source.getFeatureFlags(signal),
    enabled: Boolean(identity.customerId || identity.orgId || identity.tenantId),
    staleTime: FEATURE_FLAGS_FRESH_MS,
    refetchInterval: passive ? false : FEATURE_FLAGS_FRESH_MS,
    refetchOnWindowFocus: !passive,
  });
}

/**
 * Whether the tenancy's flags have been read (or failed to be, or there is no
 * tenancy to read them for) — so the declared defaults are the ANSWER rather
 * than a placeholder.
 *
 * For the rare surface that has to say why a feature is missing. Saying "not
 * switched on for you" in the moment before the read lands and switches it on
 * is a false statement, however briefly. Everything else reads a flag with
 * `useFeatureFlag` and simply shows the default meanwhile.
 */
export function useFeatureFlagsSettled(): boolean {
  const { isPending, fetchStatus } = useTenancyFeatureFlags({ passive: true });
  return !isPending || fetchStatus === "idle";
}

export function DashboardFeatureFlags({
  registry = FEATURE_FLAGS,
}: DashboardFeatureFlagsProps): null {
  const flags = useFeatureFlags();
  const { data } = useTenancyFeatureFlags();

  // `data` is undefined while loading and after a failed first read, which
  // `featureFlagLayer` answers with every declared default.
  const layer = React.useMemo(() => featureFlagLayer(data ?? null, registry), [data, registry]);

  React.useEffect(() => {
    flags.setClaims(layer);
  }, [flags, layer]);

  return null;
}
